<script setup lang="ts">
  import { computed, useCssModule } from 'vue';

  const classes = useCssModule();

  interface MenuItemProps {
    isSelected?: boolean;
    isDisabled?: boolean;
  }

  const props = withDefaults(defineProps<MenuItemProps>(), {
    isSelected: undefined,
    isDisabled: undefined,
  });

  const ariaSelected = computed(() => {
    if (props.isSelected === undefined) {
      return undefined;
    }
    return props.isSelected ? 'true' : 'false';
  });

  const ariaDisabled = computed(() => {
    if (props.isDisabled === undefined) {
      return undefined;
    }
    return props.isDisabled ? 'true' : undefined;
  });
</script>

<template>
  <li
    data-test="stash-menu-item"
    role="menuitem"
    class="stash-menu-item mb-1.5 first:mt-0 last:mb-0"
    :class="classes.menuitem"
    :aria-selected="ariaSelected"
    :aria-disabled="ariaDisabled"
  >
    <slot></slot>
  </li>
</template>

<style module>
  @reference "../../../styles/main.css";

  /* apply styling that allows the li or a child <a> or <button> tag to be displayed the same */
  .menuitem:not(:has(> a, > button)),
  .menuitem > :where(a, button) {
    @apply w-full
    rounded
    text-ice-700
    hover:bg-ice-200
    hover:text-ice-700
    no-underline
    hover:no-underline
    disabled:text-ice-500
    disabled:cursor-default
    transition-all
    duration-150
    ease-swing
    cursor-pointer
    text-left
    py-[10px]
    px-1.5;
  }

  .menuitem > :where(a, button) {
    display: block;
    border: 0;
  }

  .menuitem {
    @apply aria-disabled:text-ice-500
    aria-disabled:pointer-events-none
    aria-disabled:hover:text-ice-500
    aria-disabled:hover:bg-inherit
    aria-disabled:hover:cursor-default
    aria-selected:bg-blue-100;
  }

  .menuitem[aria-selected='true']:not(:global(.hidden)) {
    display: flex;
    justify-content: space-between;

    &::after {
      content: '';
      display: block;
      width: theme('spacing.5');
      height: theme('spacing.5');
      margin-left: theme('spacing.3');
      flex-shrink: 0;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%233b82f6'%3E%3Cpath fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
    }
  }
</style>
